import apiClient from '../api/client';

interface Metric {
  id: number;
  metricId: string;
  name: string;
  searchName?: string;
  definition: string;
  unit: string;
  metricType?: string;
  isAggregatable: boolean;
  impactDirection?: string;
  aggregationMethod?: string;
  updateFrequency?: string;
  clusterId?: number;
  subDimensionId?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface MetricValue {
  countryCode: string;
  countryName: string;
  value: number;
  year: number;
  dataQuality: string;
  confidenceScore?: number;
  region?: string;
  subRegion?: string;
  isBaseline: boolean;
  isScenario: boolean;
}

interface CountryMetricValuesResponse {
  metricId: string;
  metricName: string;
  unit: string;
  year: string;
  scenarioType: string;
  countries: MetricValue[];
}

class MetricsService {
  // Get all metrics with optional filters
  async getMetrics(filters?: {
    cluster?: string;
    search?: string;
    classification?: string;
    isImpactMetric?: boolean;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const params = new URLSearchParams();
    // Set default page and limit if not provided
    if (!filters?.page) {
      params.append('page', '1');
    }
    if (!filters?.limit) {
      params.append('limit', '100');
    }
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    try {
      const response = await apiClient.getRaw(`/metrics?${params.toString()}`);
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      return [];
    }
  }

  // Get a single metric by ID
  async getMetricById(metricId: string) {
    return await apiClient.get(`/metrics/${metricId}`);
  }

  // Get metric values for all countries
  async getAllCountryMetricValues(
    metricId: string,
    year?: string,
    scenarioType?: string
  ): Promise<CountryMetricValuesResponse> {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (scenarioType) params.append('scenarioType', scenarioType);
    
    return await apiClient.get(
      `/metrics/${metricId}/countries/values${params.toString() ? `?${params.toString()}` : ''}`
    );
  }

  // Get metric values for a specific country
  async getCountryMetricValues(metricId: string, countryCode: string) {
    return await apiClient.get(`/metrics/${metricId}/countries/${countryCode}/values`);
  }

  // Get metric values with filters
  async getMetricValues(
    metricId: string,
    filters?: {
      countryCode?: string;
      year?: number;
      yearFrom?: number;
      yearTo?: number;
      dataQuality?: string;
      minConfidence?: number;
      isBaseline?: boolean;
      isScenario?: boolean;
      hasValue?: boolean;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    return await apiClient.get(
      `/metrics/${metricId}/values${params.toString() ? `?${params.toString()}` : ''}`
    );
  }

  // Get clusters
  async getClusters() {
    return await apiClient.get('/metrics/clusters');
  }

  // Get subdimensions
  async getSubDimensions(clusterId?: string) {
    const url = clusterId
      ? `/metrics/clusters/${clusterId}/subdimensions`
      : '/metrics/subdimensions';
    return await apiClient.get(url);
  }

  // Get metric statistics
  async getMetricStats(countryCode?: string) {
    const params = countryCode ? `?country=${countryCode}` : '';
    return await apiClient.get(`/metrics/stats${params}`);
  }

  // Get metric aggregations
  async getMetricAggregations(metricId?: string, countryCode?: string) {
    const params = new URLSearchParams();
    if (metricId) params.append('metricId', metricId);
    if (countryCode) params.append('countryCode', countryCode);
    
    return await apiClient.get(
      `/metrics/aggregations${params.toString() ? `?${params.toString()}` : ''}`
    );
  }
}

export default new MetricsService();
export type { Metric, MetricValue, CountryMetricValuesResponse };